import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FAILURE_CATEGORY,
  FailureCodeModel,
  FailureDescription,
  FailureDialogData
} from '../../../../../go/model/form.model';
import { MAX_INPUT_LENGTH } from 'src/app/config/form.config';
import { Attachment, FilesEvent } from '../../../../../../model/file.model';
import { finalize, tap } from 'rxjs';
import { isAttachmentArray, isAttachmentUploadEvent } from '../../../../../go/utils/form.utils';
import { HttpEventType } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { StoreService } from '../../../../../../services/store.service';
import { AttachmentService } from '../../../../../../services/attachment.service';
import { DialogService } from '../../../../../../services/dialog.service';
import { MatDialogRef } from '@angular/material/dialog';
import { FailureRegisterDialogComponent } from '../../failure-register-dialog.component';

@Component({
  selector: 'failure-form-base',
  template: '',
})
export abstract class FailureFormBaseComponent {
  @Output() deleteDefectEvent = new EventEmitter();
  @Output() closeDialogEvent = new EventEmitter<FailureDescription>();
  @Input() data: FailureDialogData;
  @Input() dialogRef: MatDialogRef<FailureRegisterDialogComponent>;

  form: FormGroup;

  MAX_INPUT_LENGTH = MAX_INPUT_LENGTH ;
  FAILURE_CATEGORY = FAILURE_CATEGORY;

  noCodeOption: FailureCodeModel = { code: 'Nie dotyczy', freeText: ''} ;

  get isFailureMultiselect(): boolean {
    return this.data.failureMultiselect;
  }

  protected constructor(
    protected storeService: StoreService,
    protected attachmentService: AttachmentService,
    protected dialogService: DialogService
  ){
  }

  validatePhoto(): boolean {
    const photoControl = this.form.get('photos');
    return photoControl?.value.length > 0;
  }

  uploadFile(event: FilesEvent): void{
    const updateControlValue = (body: Attachment[]) => {
      const control = this.form.get('photos');
      let value = control?.value;
      value = [...value, ...body];
      control?.setValue(value);
    }
    void this.storeService.showLoader()
    this.attachmentService.upload(
      event.data,
      event.type,
      this.data.taskId,
      {longitude: this.data.longitude, latitude: this.data.latitude},
      stage => this.storeService.setLoaderText(stage)
    )
      .pipe(
        tap((event) => {
          if(isAttachmentUploadEvent(event)) {
            event.type === HttpEventType.UploadProgress && this.storeService.setUploadProgress(event)
          }
        }),
        tap(event => {
          if(isAttachmentArray(event)) {
            updateControlValue(event);
          }
          else {
            if(event.type === HttpEventType.Response){
              updateControlValue(event.body ?? []);
            }
          }

        }),
        finalize(() => {
          this.storeService.hideLoader();
          this.storeService.loaderText = '';
        })
      ).subscribe();
  }

  deleteFile(event: any): void {
    const control = this.form.get('photos');
    let value = control?.value;
    if(value) {
      this.storeService.showLoader();
      this.attachmentService.delete(value.id)
        .pipe(
          tap(() => control?.setValue(value.filter((v: Attachment) => v.id !== event.id))),
          finalize(() => this.storeService.hideLoader())
        )
        .subscribe();
    }
  }

  delete() {
    this.dialogService.prompt().afterClosed().subscribe(result => {
      if (result) {
        this.closeDialogEvent.emit();
        this.deleteDefectEvent.emit();
      }
    })
  }

  getDefectName(): string {
    const orderNumber = String(this.data.defectsLastNo + 1).padStart(3, '0');
    return `${this.data.taskName}_${orderNumber}`;
  }

  originalOrder = (): number => {
    return 0;
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid){

      if (!this.validatePhoto()) {
        this.showPhotoValidationMessage();
        return;
      }
      this.handleSubmit();
    }
  }

  handleSubmit() {
    this.closeDialogEvent.emit(this.preparePayload())
  }

  showPhotoValidationMessage() {
    this.storeService.showWarningMsg('PHOTO_REQUIRED')
  }

  preparePayload(): FailureDescription {
    const payload = this.form.value as FailureDescription;
    payload.id = (this.data.failureDescription as FailureDescription)?.id;
    payload.failureCodes = this.form.value.failureCodes instanceof Array
      ?  (this.form.value.failureCodes ?? [])
      : (this.form.value.failureCodes ? [this.form.value.failureCodes] : []);
    payload.latitude = this.data.latitude;
    payload.longitude = this.data.longitude;
    return payload;
  }

  fillFormWithData(): void {
    if (this.data.failureDescription) {
      this.form.patchValue(
        this.isFailureMultiselect
          ? this.data.failureDescription
          : {...this.data.failureDescription, failureCodes: this.data.failureDescription.failureCodes[0]}
      );
    } else if (this.data.failureCodeList.length === 1) {
      this.form.patchValue({
        failureCodes: this.data.failureMultiselect ? [] : this.data.failureCodeList[0]
      });
    }
  }

  compareFailureCodes(code1: FailureCodeModel, code2: FailureCodeModel): boolean {
    return code1.code === code2?.code;
  };
}
